import {
  ENTITY_CLASS,
  ERROR_CODE,
  FIM_DOMAIN,
  type FimDomain,
  type IndependenceLevelEntryQuery,
  UniversalError,
  type UserKey,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

import { fetchData, findIndependenceLevelEntries } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

export async function findLatestIndependenceLevelEntry(
  userKey: UserKey | false | null | undefined,
) {
  if (!userKey) {
    return undefined;
  }
  const { output } = await fetchData({
    type: "Entity::IndependenceLevelEntry",
    input: { condition: [["userKey", "=", userKey]], descendingOrder: true },
  });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function LatestIndependenceLevelEntryQueryKey(userKey: UserKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.INDEPENDENCE_LEVEL_ENTRY + "::Latest", userKey);
}

export function updateLatestIndependenceLevelEntryQueryCache(
  userKey: UserKey | false | null | undefined,
  newIndependenceLevelEntry?:
    ReturnType<typeof useLatestIndependenceLevelEntryQuery>["data"] | false,
) {
  if (userKey && newIndependenceLevelEntry !== false && newIndependenceLevelEntry !== undefined) {
    const queryKey = LatestIndependenceLevelEntryQueryKey(userKey);
    TANSTACK_QUERY_CLIENT.setQueryData(
      queryKey,
      function (
        oldIndependenceLevelEntry: ReturnType<typeof useLatestIndependenceLevelEntryQuery>["data"],
      ) {
        return !oldIndependenceLevelEntry ||
          !newIndependenceLevelEntry ||
          (Temporal.PlainDateTime.compare(
            oldIndependenceLevelEntry.assessedAt,
            newIndependenceLevelEntry.assessedAt,
          ) ||
            Temporal.PlainDateTime.compare(
              oldIndependenceLevelEntry.createdAt,
              newIndependenceLevelEntry.createdAt,
            ) ||
            Temporal.PlainDateTime.compare(
              oldIndependenceLevelEntry.updatedAt,
              newIndependenceLevelEntry.updatedAt,
            )) < 0
          ? newIndependenceLevelEntry
          : oldIndependenceLevelEntry;
      },
    );
  }
}

export function useLatestIndependenceLevelEntryQuery(userKey: UserKey | false | null | undefined) {
  return useQuery({
    queryKey: LatestIndependenceLevelEntryQueryKey(userKey),
    async queryFn() {
      return findLatestIndependenceLevelEntry(userKey);
    },

    enabled: Boolean(userKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

function IndependenceLevelEntryInfiniteQueryKey(
  ...parameters: Parameters<typeof useIndependenceLevelEntryInfiniteQuery>
) {
  return QueryKey(ENTITY_CLASS.INDEPENDENCE_LEVEL_ENTRY + "List", ...parameters);
}

export function removeIndependenceLevelEntryInfiniteQueryCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({
    queryKey: IndependenceLevelEntryInfiniteQueryKey({}).slice(0, 1),
  });
}

export function useIndependenceLevelEntryInfiniteQuery(
  query: IndependenceLevelEntryQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: IndependenceLevelEntryInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { independenceLevelEntries, nextPageToken } = await findIndependenceLevelEntries({
          ...query,
          ...context.pageParam,
        });

        for (const independenceLevelEntry of independenceLevelEntries) {
          updateLatestIndependenceLevelEntryQueryCache(
            independenceLevelEntry.userKey,
            independenceLevelEntry,
          );
        }

        return { independenceLevelEntries, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

export function fromFimDomainToColor(fimDomain: FimDomain) {
  switch (fimDomain) {
    case FIM_DOMAIN.COMMUNICATION:
      return "#16A34A";
    case FIM_DOMAIN.LOCOMOTION:
      return "#0891B2";
    case FIM_DOMAIN.SELF_CARE:
      return "#DB2777";
    case FIM_DOMAIN.SOCIAL_COGNITION:
      return "#EA580C";
    case FIM_DOMAIN.SPHINCTER_CONTROL:
      return "#9333EA";
    case FIM_DOMAIN.TRANSFER:
      return "#4F46E5";
    default:
      throw new UniversalError(
        ERROR_CODE.UNIMPLEMENTED,
        "FIMサブドメイン" + (fimDomain satisfies never) + "の色設定が実装されていません。",
      );
  }
}
